import { useCallback, useEffect, useState } from 'react';

import { savePrompt } from '../lib/actions';
import { fetchPrompts } from '../lib/brain';
import { supabase } from '../lib/supabase';

/**
 * The reply prompt, on the Setup page, editable. One prompt, not three.
 *
 * ## Why this does not depend on the Worker any more
 *
 * It used to load through `GET /admin/prompts`, because that is the only place the *built-in* text
 * lives and the old version refused to keep a copy of it in this bundle. The consequence was that a
 * Worker the dashboard could not reach - a missing or stale bridge function, a Worker that is not
 * running - disabled the whole section: no box, just an error and a Try again button. The prompt is
 * what the product says, and one unreachable service should not be able to take the editor away.
 *
 * So the read and the write now go to `settings.prompt_overrides` directly, over the anon key and
 * RLS, exactly like the watchlist next door. The Worker is still asked for the built-in text, but
 * only to have something to show and something to fall back to: when it does not answer, the box
 * simply starts from whatever is saved, and saving still works.
 *
 * A blank box means "go back to the built-in prompt". The Worker treats a blank override as absent,
 * so clearing it really does restore the shipped text.
 */

interface Props {
  actor: string;
}

export default function PromptsSection({ actor }: Props) {
  /** The built-in text and version, when the Worker answered. Null when it did not. */
  const [builtin, setBuiltin] = useState<{ version: string; effective: string } | null>(null);
  /** What is saved in the database right now. Null means the built-in prompt is in use. */
  const [saved, setSaved] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Why the built-in text is not on screen. Only ever informational. */
  const [builtinNote, setBuiltinNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    // 1. What is saved. This is the part that must work, and it needs nothing but the anon key.
    const { data, error: readError } = await supabase
      .from('settings')
      .select('prompt_overrides')
      .eq('id', 1)
      .maybeSingle();

    if (readError) {
      setError(readError.message);
      setLoading(false);
      return;
    }

    const overrides = (data?.prompt_overrides ?? {}) as { prompt?: string };
    const savedPrompt =
      typeof overrides.prompt === 'string' && overrides.prompt.trim().length > 0
        ? overrides.prompt
        : null;
    setSaved(savedPrompt);

    // 2. The built-in text, which only the Worker has. Nice to have; failure is not an error here.
    const result = await fetchPrompts();
    const worker = result.ok && result.data ? result.data : null;
    setBuiltin(worker ? { version: worker.version, effective: worker.effective } : null);
    setBuiltinNote(
      worker ? null : (result.error ?? 'The Worker did not answer, so the built-in text is not shown.'),
    );

    setText(savedPrompt ?? worker?.effective ?? '');
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** What is currently stored differs from what is in the box. */
  const changed = text.trim() !== (saved ?? '').trim() || (text.trim().length === 0 && saved !== null);

  async function save() {
    setBusy(true);
    setStatus(null);
    setError(null);

    const blank = text.trim().length === 0;
    const result = await savePrompt(actor, blank ? null : text);

    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'Could not save the prompt.');
      return;
    }

    setSaved(blank ? null : text);
    if (blank) setText(builtin?.effective ?? '');
    setStatus(
      blank
        ? 'Cleared. The built-in prompt is in use again.'
        : 'Saved. It applies to the next draft, not to the replies already in the queue.',
    );
  }

  return (
    <section className="setup-section">
      <h3>Reply prompt</h3>
      <p className="muted small">
        The exact instructions the reply writer gets. Edit it, then <b>Save</b>. It applies to the next
        draft; replies already in the queue keep the wording they were written with — use{' '}
        <b>Update reply</b> on a card to rewrite one.
      </p>

      {status ? (
        <p className="notice" role="status">
          {status}
        </p>
      ) : null}
      {error ? <p className="error">{error}</p> : null}

      {loading ? <p className="muted">Loading the prompt…</p> : null}

      {!loading ? (
        <div className="prompt-lane">
          <div className="prompt-head">
            <span>Reply prompt</span>
            <span className="badge">{saved ? 'edited' : 'built-in'}</span>
            {builtin ? <span className="muted small">built-in {builtin.version}</span> : null}
          </div>

          {builtinNote ? (
            <p className="muted small">
              {builtinNote} You can still write and save a prompt: whatever you save replaces the
              built-in text outright.
            </p>
          ) : null}

          {!saved && !builtin ? (
            <p className="muted small">
              Nothing is saved yet, so the built-in prompt — which lives in the Worker — is what runs.
              Type your version below and save it to take over.
            </p>
          ) : null}

          <textarea
            aria-label="Reply prompt"
            className="prompt-text"
            value={text}
            disabled={busy}
            spellCheck={false}
            onChange={(event) => setText(event.target.value)}
          />

          <div className="prompt-foot">
            <span className="muted small">
              {text.length} characters
              {saved ? '' : builtin ? ' · showing the built-in text' : ''}
            </span>
            <span className="grow" />
            {saved ? (
              <button
                type="button"
                className="secondary"
                disabled={busy}
                title="Clear the saved prompt and go back to the built-in text"
                onClick={() => {
                  setText('');
                  void savePrompt(actor, null).then((result) => {
                    if (!result.ok) {
                      setError(result.error ?? 'Could not clear the prompt.');
                      return;
                    }
                    setSaved(null);
                    setText(builtin?.effective ?? '');
                    setStatus('Cleared. The built-in prompt is in use again.');
                  });
                }}
              >
                Reset to built-in
              </button>
            ) : null}
            <button type="button" disabled={busy || !changed} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Save prompt'}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
